import { useEffect, useRef } from "react";
import { useCookieConsent } from "@/context/CookieConsentContext";

/**
 * AnalyticsLoader — loads Google Analytics 4 (gtag.js) ONLY when the user
 * has accepted the "analytics" cookie category.
 *
 * - On consent.analytics === true: injects gtag.js, initializes dataLayer and
 *   configures GA with the Measurement ID from REACT_APP_GA_MEASUREMENT_ID.
 * - On consent.analytics === false (or withdrawn): removes the script tag,
 *   neutralizes gtag, and clears any _ga* cookies that GA may have set.
 *
 * Configure the Measurement ID in /app/frontend/.env:
 *   REACT_APP_GA_MEASUREMENT_ID=G-XXXXXXXXXX
 */

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "G-XXXXXXXXXX";
const SCRIPT_ID = "ga4-gtag-loader";
const INLINE_ID = "ga4-gtag-init";
const IS_PLACEHOLDER = GA_ID === "G-XXXXXXXXXX";

function clearGaCookies() {
  if (typeof document === "undefined") return;
  const host = window.location.hostname;
  // GA sets _ga, _ga_<container>, _gid, _gat
  const parts = host.split(".");
  const domains = [host];
  if (parts.length > 1) domains.push("." + parts.slice(-2).join("."));
  document.cookie.split(";").forEach((raw) => {
    const name = raw.split("=")[0].trim();
    if (!/^_ga|^_gid|^_gat/.test(name)) return;
    domains.forEach((d) => {
      document.cookie = `${name}=; Max-Age=0; path=/; domain=${d}`;
    });
    document.cookie = `${name}=; Max-Age=0; path=/`;
  });
}

function injectGa() {
  if (document.getElementById(SCRIPT_ID)) return; // already loaded
  // 1) Async loader
  const loader = document.createElement("script");
  loader.id = SCRIPT_ID;
  loader.async = true;
  loader.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`;
  document.head.appendChild(loader);

  // 2) Inline init
  const inline = document.createElement("script");
  inline.id = INLINE_ID;
  inline.text = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('config', '${GA_ID}', { anonymize_ip: true });
  `;
  document.head.appendChild(inline);
}

function removeGa() {
  document.getElementById(SCRIPT_ID)?.remove();
  document.getElementById(INLINE_ID)?.remove();
  // Best-effort: stop further events
  if (typeof window !== "undefined") {
    window["ga-disable-" + GA_ID] = true;
    // Replace gtag with a noop so any queued or future calls are inert
    window.gtag = function () {};
    window.dataLayer = [];
  }
  clearGaCookies();
}

export default function AnalyticsLoader() {
  const { consent } = useCookieConsent();
  const loadedRef = useRef(false);

  useEffect(() => {
    if (IS_PLACEHOLDER) return; // Don't load with placeholder ID in dev/preview
    const allowed = !!consent?.analytics;
    if (allowed && !loadedRef.current) {
      injectGa();
      loadedRef.current = true;
    } else if (!allowed && loadedRef.current) {
      removeGa();
      loadedRef.current = false;
    }
  }, [consent?.analytics]);

  return null;
}
